/**
 * Route catalogue — the single source of truth for portal route paths and the
 * permission policy that guards them.
 *
 * Why this exists
 * ---------------
 * `AppRoutes` (enforcement), `Sidebar` (navigation) and the navigation
 * registry (search / command palette / AI intents) each used to declare their
 * own copy of a path string and its required permission. They drifted. This
 * module owns both facts once, and those three surfaces read from it.
 *
 * Rules
 * -----
 * - `ROUTES` mirrors every `<Route path=...>` declared in `AppRoutes`. The
 *   `ProtectedRoute` permissions recorded here ARE the enforced policy.
 * - `NAV_ONLY` holds paths that navigation surfaces link to but `AppRoutes`
 *   does not declare directly (children of the `/openbanking/*` sub-router, and
 *   links whose page does not exist yet). Their access mirrors whatever route
 *   actually enforces them, or their existing navigation policy when nothing
 *   does.
 * - This module is data + pure lookup only. It performs no authorization; it
 *   states policy. `ProtectedRoute` and the backend remain the enforcement
 *   points.
 */

import { PERMISSIONS, type PermissionName } from '../constants/permissions';

// ─── Types ───────────────────────────────────────────────────────────────────

/** How a route is guarded. */
export type RouteAccess =
    | { readonly kind: 'unrestricted' }
    | {
        readonly kind: 'permission';
        readonly permissions: PermissionName[];
        /** ANY of `permissions` is enough. Default is ALL. */
        readonly requireAny: boolean;
    };

export interface RouteDefinition {
    /** React Router path pattern. May contain `:params` or a trailing `*`. */
    readonly path: string;
    readonly access: RouteAccess;
}

// ─── Builders ────────────────────────────────────────────────────────────────

/** A route any authenticated user may open. */
const open = (path: string): RouteDefinition => ({
    path,
    access: { kind: 'unrestricted' },
});

/** A route guarded by permissions. */
const guard = (
    path: string,
    permissions: PermissionName[],
    options?: { requireAny: true },
): RouteDefinition => ({
    path,
    access: { kind: 'permission', permissions, requireAny: options?.requireAny ?? false },
});

// ─── Routes declared by AppRoutes ────────────────────────────────────────────
// Order mirrors AppRoutes for reviewability.

export const ROUTES = {
    ROOT: open('/'),
    DASHBOARD: open('/dashboard'),
    CUSTOMERS: guard('/customers', [PERMISSIONS.CUSTOMERS_VIEW]),
    CUSTOMERS_DETAIL: guard('/customers/:customerId', [PERMISSIONS.CUSTOMERS_VIEW]),
    VEHICLE_LOANS: guard('/vehicle-loans', [PERMISSIONS.LOANS_VIEW]),
    PAYMENTS_BANK_TRANSFER: guard('/payments/bank-transfer', [PERMISSIONS.TRANSACTIONS_CREATE]),
    OPENBANKING_SECTION: guard('/openbanking/*', [PERMISSIONS.ACCOUNTS_VIEW]),
    ACCOUNTS: guard('/accounts', [PERMISSIONS.ACCOUNTS_VIEW]),
    ACCOUNTS_DETAIL: guard('/accounts/:id', [PERMISSIONS.ACCOUNTS_VIEW]),
    ACCOUNTS_NEW: guard('/accounts/new', [PERMISSIONS.ACCOUNTS_CREATE]),
    INSTITUTIONS_NEW: guard('/institutions/new', [PERMISSIONS.PLATFORM_INSTITUTIONS_CREATE]),
    INSTITUTIONS: guard('/institutions', [PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]),
    INSTITUTIONS_DETAIL: guard('/institutions/:id', [PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]),
    INSTITUTIONS_DETAIL_APP_CONFIG: guard('/institutions/:id/app-config', [PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE]),
    USERS: guard('/users', [PERMISSIONS.USERS_VIEW]),
    USERS_DETAIL: guard('/users/:id', [PERMISSIONS.USERS_VIEW]),
    USERS_DETAIL_EDIT: guard('/users/:id/edit', [PERMISSIONS.USERS_EDIT, PERMISSIONS.USERS_ASSIGN_ROLES], { requireAny: true }),
    PROFILE: open('/profile'),
    /** Canonical deposit products page under the `/deposits/*` namespace. */
    DEPOSITS_PRODUCTS: guard('/deposits/products', [PERMISSIONS.SAVINGS_VIEW]),
    /** Legacy alias; AppRoutes redirects to `DEPOSITS_PRODUCTS` with the same policy. */
    DEPOSIT_PRODUCTS_LEGACY: guard('/deposit-products', [PERMISSIONS.SAVINGS_VIEW]),
    DEPOSITS_ACCOUNTS: guard('/deposits/accounts', [PERMISSIONS.SAVINGS_VIEW]),
    DEPOSITS_RATES: guard('/deposits/rates', [PERMISSIONS.SAVINGS_MANAGE]),
    DEPOSITS_MATURITIES: guard('/deposits/maturities', [PERMISSIONS.SAVINGS_VIEW]),
    DEPOSITS_APPLICATIONS: guard('/deposits/applications', [PERMISSIONS.SAVINGS_VIEW]),
    DEPOSITS_NOTICES: guard('/deposits/notices', [PERMISSIONS.SAVINGS_VIEW]),
    DEPOSITS_REPORTS: guard('/deposits/reports', [PERMISSIONS.SAVINGS_VIEW]),
    DEPOSITS_SETTINGS: guard('/deposits/settings', [PERMISSIONS.SAVINGS_MANAGE]),
    DEPOSITS_TRANSFERS: guard('/deposits/transfers', [PERMISSIONS.SAVINGS_VIEW]),
    API_MANAGEMENT: guard('/api-management', [PERMISSIONS.SETTINGS_SECURITY]),
    GATEWAY_ROUTES: guard('/gateway/routes', [PERMISSIONS.PLATFORM_ROUTES_MANAGE]),
    GATEWAY_ROUTES_NEW: guard('/gateway/routes/new', [PERMISSIONS.PLATFORM_ROUTES_MANAGE]),
    GATEWAY_ROUTES_DETAIL: guard('/gateway/routes/:routeId', [PERMISSIONS.PLATFORM_ROUTES_MANAGE]),
    LOANS: guard('/loans', [PERMISSIONS.LOANS_VIEW]),
    LOANS_PIPELINE: guard('/loans/pipeline', [PERMISSIONS.LOANS_VIEW]),
    LOANS_APPLICATIONS: guard('/loans/applications', [PERMISSIONS.LOANS_VIEW]),
    LOANS_APPLICATIONS_NEW: guard('/loans/applications/new', [PERMISSIONS.LOANS_CREATE]),
    LOANS_APPLICATIONS_DETAIL: guard('/loans/applications/:id', [PERMISSIONS.LOANS_VIEW]),
    LOANS_APPLICATIONS_DETAIL_DETAIL: guard('/loans/applications/:id/detail', [PERMISSIONS.LOANS_VIEW]),
    LOANS_CREDIT_DECISIONING: guard('/loans/credit-decisioning', [PERMISSIONS.LOANS_APPROVE]),
    LOANS_DISBURSEMENT: guard('/loans/disbursement', [PERMISSIONS.LOANS_DISBURSE]),
    LOANS_PORTFOLIO: guard('/loans/portfolio', [PERMISSIONS.LOANS_VIEW]),
    LOANS_PORTFOLIO_DETAIL: guard('/loans/portfolio/:id', [PERMISSIONS.LOANS_VIEW]),
    LOANS_ACCOUNTS: guard('/loans/accounts', [PERMISSIONS.ACCOUNTS_VIEW]),
    LOANS_ACCOUNTS_DETAIL: guard('/loans/accounts/:id', [PERMISSIONS.ACCOUNTS_VIEW]),
    LOANS_COLLECTIONS: guard('/loans/collections', [PERMISSIONS.LOANS_MANAGE]),
    LOANS_COLLECTIONS_DETAIL: guard('/loans/collections/:id', [PERMISSIONS.LOANS_MANAGE]),
    LOANS_PRODUCTS: guard('/loans/products', [PERMISSIONS.LOANS_VIEW]),
    LOANS_REPORTS: guard('/loans/reports', [PERMISSIONS.REPORTS_VIEW]),
    MORTGAGES_PIPELINE: guard('/mortgages/pipeline', [PERMISSIONS.MORTGAGE_VIEW]),
    MORTGAGES_APPLICATIONS_DETAIL: guard('/mortgages/applications/:id', [PERMISSIONS.MORTGAGE_VIEW]),
    MORTGAGES_PRODUCTS: guard('/mortgages/products', [PERMISSIONS.MORTGAGE_VIEW]),
    MORTGAGES_PORTFOLIO: guard('/mortgages/portfolio', [PERMISSIONS.MORTGAGE_VIEW]),
    MORTGAGES_PORTFOLIO_DETAIL: guard('/mortgages/portfolio/:id', [PERMISSIONS.MORTGAGE_VIEW]),
    MORTGAGES_AFFORDABILITY: guard('/mortgages/affordability', [PERMISSIONS.MORTGAGE_VIEW]),
    MORTGAGES_REPORTS: guard('/mortgages/reports', [PERMISSIONS.MORTGAGE_VIEW]),
    CARDS: guard('/cards', [PERMISSIONS.CARDS_VIEW]),
    CARDS_DISPUTES: guard('/cards/disputes', [PERMISSIONS.CARDS_MANAGE]),
    CARDS_DETAIL: guard('/cards/:id', [PERMISSIONS.CARDS_VIEW]),
    SMART_PANEL: guard('/smart-panel', [PERMISSIONS.SETTINGS_EDIT]),
    SMART_PANEL_CREATE: guard('/smart-panel/create', [PERMISSIONS.SETTINGS_EDIT]),
    SMART_PANEL_DETAIL_EDIT: guard('/smart-panel/:messageId/edit', [PERMISSIONS.SETTINGS_EDIT]),
    REWARDS_CONFIG: guard('/rewards/config', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    REWARDS_BADGES: guard('/rewards/badges', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    REWARDS_TIERS: guard('/rewards/tiers', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    REWARDS_OFFERS: guard('/rewards/offers', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    REWARDS_ANALYTICS: guard('/rewards/analytics', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    INSIGHTS: guard('/insights', [PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]),
    JOURNEY_TEMPLATES: guard('/journey-templates', [PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]),
    JOURNEY_TEMPLATES_NEW: guard('/journey-templates/new', [PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]),
    JOURNEY_TEMPLATES_DETAIL: guard('/journey-templates/:templateId', [PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]),
    PROFILE_TRAITS: guard('/profile-traits', [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]),
    BRANCH_DASHBOARD: guard('/branch/dashboard', [PERMISSIONS.BRANCH_BANKING_ACCESS]),
    BRANCH_CUSTOMERS: guard('/branch/customers', [PERMISSIONS.BRANCH_BANKING_ACCESS]),
    BRANCH_ACCOUNTS_OPEN: guard('/branch/accounts/open', [PERMISSIONS.BRANCH_ACCOUNTS_OPEN]),
    BRANCH_TRANSACTIONS: guard('/branch/transactions', [PERMISSIONS.BRANCH_TRANSACTIONS_CREATE]),
    BRANCH_CASH: guard('/branch/cash', [PERMISSIONS.BRANCH_CASH_MANAGE]),
    BRANCH_QUEUE: guard('/branch/queue', [PERMISSIONS.BRANCH_BANKING_ACCESS]),
    AUDIT: guard('/audit', [PERMISSIONS.AUDIT_VIEW]),
    AUDIT_LOG: guard('/audit/log', [PERMISSIONS.AUDIT_VIEW]),
    AUDIT_USERS: guard('/audit/users', [PERMISSIONS.AUDIT_VIEW]),
    AUDIT_REPORT_BUILDER: guard('/audit/report-builder', [PERMISSIONS.AUDIT_VIEW]),
    GENERAL_LEDGER: guard('/general-ledger', [PERMISSIONS.GL_VIEW]),
    GENERAL_LEDGER_ACCOUNTS: guard('/general-ledger/accounts', [PERMISSIONS.GL_VIEW]),
    GENERAL_LEDGER_JOURNALS: guard('/general-ledger/journals', [PERMISSIONS.GL_VIEW]),
    GENERAL_LEDGER_JOURNALS_DETAIL: guard('/general-ledger/journals/:id', [PERMISSIONS.GL_VIEW]),
    GENERAL_LEDGER_TRIAL_BALANCE: guard('/general-ledger/trial-balance', [PERMISSIONS.GL_VIEW]),
    PAYMENT_OPS: guard('/payment-ops', [PERMISSIONS.PAYMENTS_VIEW]),
    PAYMENT_OPS_SEARCH: guard('/payment-ops/search', [PERMISSIONS.PAYMENTS_VIEW]),
    PAYMENT_OPS_RECONCILIATION: guard('/payment-ops/reconciliation', [PERMISSIONS.PAYMENTS_RECONCILE]),
    PAYMENT_OPS_STANDING_ORDERS: guard('/payment-ops/standing-orders', [PERMISSIONS.PAYMENTS_VIEW]),
    PAYMENT_OPS_FAILED: guard('/payment-ops/failed', [PERMISSIONS.PAYMENTS_MANAGE]),
    PAYMENT_OPS_SCHEME_ROUTING: guard('/payment-ops/scheme-routing', [PERMISSIONS.PAYMENTS_SCHEME_CONFIG]),
    PAYMENTS_HUB: guard('/payments-hub', [PERMISSIONS.PAYMENTS_VIEW]),
    PAYMENTS_HUB_INITIATE: guard('/payments-hub/initiate', [PERMISSIONS.PAYMENTS_MANAGE]),
    PAYMENTS_HUB_SCHEMES: guard('/payments-hub/schemes', [PERMISSIONS.PAYMENTS_SCHEME_CONFIG]),
    PAYMENTS_HUB_STANDING_ORDERS: guard('/payments-hub/standing-orders', [PERMISSIONS.PAYMENTS_VIEW]),
    PAYMENTS_HUB_DIRECT_DEBITS: guard('/payments-hub/direct-debits', [PERMISSIONS.PAYMENTS_VIEW]),
    PAYMENTS_HUB_DETAIL: guard('/payments-hub/:id', [PERMISSIONS.PAYMENTS_VIEW]),
    GL_ACCOUNTS: guard('/gl/accounts', [PERMISSIONS.GL_VIEW]),
    GL_JOURNALS: guard('/gl/journals', [PERMISSIONS.GL_VIEW]),
    GL_TRIAL_BALANCE: guard('/gl/trial-balance', [PERMISSIONS.GL_VIEW]),
    GL_FINANCIAL_STATEMENTS: guard('/gl/financial-statements', [PERMISSIONS.GL_VIEW]),
    GL_COST_CENTRES: guard('/gl/cost-centres', [PERMISSIONS.GL_VIEW]),
    GL_RECONCILIATION: guard('/gl/reconciliation', [PERMISSIONS.PAYMENTS_RECONCILE]),
    GL_PERIODS: guard('/gl/periods', [PERMISSIONS.GL_MANAGE]),
    KYC: guard('/kyc', [PERMISSIONS.COMPLIANCE_VIEW]),
    KYC_REVIEWS: guard('/kyc/reviews', [PERMISSIONS.COMPLIANCE_MANAGE]),
    KYC_ANALYTICS: guard('/kyc/analytics', [PERMISSIONS.COMPLIANCE_VIEW]),
    COMPLIANCE: guard('/compliance', [PERMISSIONS.COMPLIANCE_VIEW]),
    COMPLIANCE_ALERTS: guard('/compliance/alerts', [PERMISSIONS.COMPLIANCE_VIEW]),
    COMPLIANCE_SAR: guard('/compliance/sar', [PERMISSIONS.COMPLIANCE_SAR_FILE]),
    COMPLIANCE_REGULATORY_RETURNS: guard('/compliance/regulatory-returns', [PERMISSIONS.COMPLIANCE_VIEW]),
    COMPLIANCE_SANCTIONS: guard('/compliance/sanctions', [PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN]),
    COMPLIANCE_SAR_MANAGEMENT: guard('/compliance/sar-management', [PERMISSIONS.COMPLIANCE_SAR_FILE]),
    COMPLIANCE_SANCTIONS_SCREENING: guard('/compliance/sanctions-screening', [PERMISSIONS.COMPLIANCE_SANCTIONS_SCREEN]),
    COMPLIANCE_TRANSACTION_ALERTS: guard('/compliance/transaction-alerts', [PERMISSIONS.COMPLIANCE_VIEW]),
    COMPLIANCE_REGULATORY_REPORTS: guard('/compliance/regulatory-reports', [PERMISSIONS.REGULATORY_REPORTS_VIEW]),
    COMPLIANCE_CAPITAL_ADEQUACY: guard('/compliance/capital-adequacy', [PERMISSIONS.REGULATORY_CAPITAL_VIEW]),
    COMPLIANCE_IFRS9: guard('/compliance/ifrs9', [PERMISSIONS.REGULATORY_REPORTS_VIEW]),
    COMPLIANCE_RWA: guard('/compliance/rwa', [PERMISSIONS.REGULATORY_CAPITAL_VIEW]),
    KYB_ENTITIES: guard('/kyb/entities', [PERMISSIONS.KYB_ENTITIES_VIEW]),
    KYB_ENTITIES_DETAIL: guard('/kyb/entities/:id', [PERMISSIONS.KYB_ENTITIES_VIEW]),
    KYB_REVIEWS: guard('/kyb/reviews', [PERMISSIONS.KYB_REVIEWS_VIEW]),
    KYB_SCREENING: guard('/kyb/screening', [PERMISSIONS.KYB_SCREENING_VIEW]),
    KYB_OWNERSHIP_DETAIL: guard('/kyb/ownership/:id', [PERMISSIONS.KYB_ENTITIES_VIEW]),
    BRANCH_TELLER: guard('/branch/teller', [PERMISSIONS.BRANCH_BANKING_ACCESS]),
    BRANCH_VAULT: guard('/branch/vault', [PERMISSIONS.BRANCH_CASH_MANAGE]),
    BRANCH_EOD: guard('/branch/eod', [PERMISSIONS.BRANCH_CASH_MANAGE]),
    BRANCH_OVERVIEW: guard('/branch/overview', [PERMISSIONS.BRANCH_BANKING_ACCESS]),
    NOTIFICATIONS_TEMPLATES: guard('/notifications/templates', [PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE]),
    NOTIFICATIONS_HISTORY: guard('/notifications/history', [PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW]),
    NOTIFICATIONS_CHANNELS: guard('/notifications/channels', [PERMISSIONS.INSTITUTION_NOTIFICATIONS_MANAGE]),
    NOTIFICATIONS_PREFERENCES: guard('/notifications/preferences', [PERMISSIONS.INSTITUTION_NOTIFICATIONS_VIEW]),
    PRODUCTS: guard('/products', [PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]),
    PRODUCTS_COMPARE: guard('/products/compare', [PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]),
    PRODUCTS_DETAIL: guard('/products/:id', [PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]),
    PRODUCTS_BUILDER: guard('/products/builder', [PERMISSIONS.PRODUCTS_DEFINITIONS_VIEW]),
    LIMITS: guard('/limits', [PERMISSIONS.LIMITS_VIEW]),
    LIMITS_INSTITUTION: guard('/limits/institution', [PERMISSIONS.LIMITS_VIEW]),
    LIMITS_CUSTOMER_OVERRIDES: guard('/limits/customer-overrides', [PERMISSIONS.LIMITS_VIEW]),
    LIMITS_HISTORY: guard('/limits/history', [PERMISSIONS.LIMITS_VIEW]),
    ADMIN_INSIGHTS_DESIGNER: guard('/admin/insights/designer', [PERMISSIONS.INSTITUTION_INSIGHTS_MANAGE]),
    ADMIN_INSIGHTS_RULES: guard('/admin/insights/rules', [PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]),
    ADMIN_INSIGHTS_SCHEDULES: guard('/admin/insights/schedules', [PERMISSIONS.INSTITUTION_INSIGHTS_VIEW]),
    ADMIN_PROFILES_DESIGNER: guard('/admin/profiles/designer', [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_MANAGE]),
    ADMIN_PROFILES_TRAITS: guard('/admin/profiles/traits', [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]),
    ADMIN_PROFILES_OVERRIDES: guard('/admin/profiles/overrides', [PERMISSIONS.INSTITUTION_PROFILE_TRAITS_VIEW]),
    ADMIN_JOURNEYS_TEMPLATES: guard('/admin/journeys/templates', [PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]),
    ADMIN_JOURNEYS_BUILDER_DETAIL: guard('/admin/journeys/builder/:id', [PERMISSIONS.INSTITUTION_JOURNEYS_MANAGE]),
    ADMIN_JOURNEYS_VERSIONS_DETAIL: guard('/admin/journeys/versions/:id', [PERMISSIONS.INSTITUTION_JOURNEYS_VIEW]),
    ADMIN_PROVIDERS: guard('/admin/providers', [PERMISSIONS.PROVIDERS_CATALOG_VIEW]),
    ADMIN_PROVIDERS_CONTRACTS: guard('/admin/providers/contracts', [PERMISSIONS.PROVIDERS_CONTRACTS_VIEW]),
    ADMIN_PROVIDERS_HEALTH: guard('/admin/providers/health', [PERMISSIONS.PROVIDERS_HEALTH_VIEW]),
    ADMIN_PROVIDERS_FEATURES: guard('/admin/providers/features', [PERMISSIONS.PROVIDERS_CATALOG_VIEW]),
    ADMIN_PROVIDERS_SWAP: guard('/admin/providers/swap', [PERMISSIONS.PROVIDERS_SWAP]),
    ADMIN_PROVIDERS_METERING: guard('/admin/providers/metering', [PERMISSIONS.PROVIDERS_METERING_VIEW]),
    ADMIN_FEES: guard('/admin/fees', [PERMISSIONS.FEES_DEFINITIONS_VIEW]),
    ADMIN_FEES_SCHEDULES: guard('/admin/fees/schedules', [PERMISSIONS.FEES_SCHEDULES_VIEW]),
    ADMIN_FEES_SIMULATOR: guard('/admin/fees/simulator', [PERMISSIONS.FEES_SIMULATE]),
    ADMIN_REWARDS: guard('/admin/rewards', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    ADMIN_REWARDS_BADGES: guard('/admin/rewards/badges', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    ADMIN_REWARDS_MILESTONES: guard('/admin/rewards/milestones', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    ADMIN_REWARDS_SCHEMES: guard('/admin/rewards/schemes', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    ADMIN_REWARDS_ANALYTICS: guard('/admin/rewards/analytics', [PERMISSIONS.INSTITUTION_REWARDS_VIEW]),
    ADMIN_OPENBANKING_CONFIG: guard('/admin/openbanking/config', [PERMISSIONS.OPENBANKING_CONFIG_VIEW]),
    ADMIN_OPENBANKING_CONNECTIONS: guard('/admin/openbanking/connections', [PERMISSIONS.OPENBANKING_CONNECTIONS_VIEW]),
    ADMIN_OPENBANKING_ALERTS: guard('/admin/openbanking/alerts', [PERMISSIONS.OPENBANKING_ALERTS_VIEW]),
    ADMIN_OPENBANKING_STATS: guard('/admin/openbanking/stats', [PERMISSIONS.OPENBANKING_STATS_VIEW]),
    ADMIN_OPENBANKING_CONSENTS: guard('/admin/openbanking/consents', [PERMISSIONS.OPENBANKING_CONSENTS_VIEW]),
    ADMIN_CONFIG: guard('/admin/config', [PERMISSIONS.CONFIG_VIEW]),
    ADMIN_CONFIG_HISTORY: guard('/admin/config/history', [PERMISSIONS.CONFIG_VIEW]),
    ADMIN_CONFIG_EXPORT_IMPORT: guard('/admin/config/export-import', [PERMISSIONS.CONFIG_EXPORT]),
    ADMIN_RULES: guard('/admin/rules', [PERMISSIONS.SETTINGS_EDIT]),
    ADMIN_NEURAL_PERSONALITY: guard('/admin/neural/personality', [PERMISSIONS.NEURAL_PERSONALITY_VIEW]),
    ADMIN_NEURAL_RULES: guard('/admin/neural/rules', [PERMISSIONS.NEURAL_RULES_VIEW]),
    ADMIN_NEURAL_FEEDBACK: guard('/admin/neural/feedback', [PERMISSIONS.NEURAL_FEEDBACK_VIEW]),
    ADMIN_NEURAL_CONVERSATIONS: guard('/admin/neural/conversations', [PERMISSIONS.NEURAL_SESSIONS_VIEW]),
    ADMIN_MONI_AI_FEEDBACK: guard('/admin/moni-ai/feedback', [PERMISSIONS.INSTITUTION_MONI_FEEDBACK_VIEW]),
    PLATFORM_HEALTH: guard('/platform/health', [PERMISSIONS.PLATFORM_HEALTH_VIEW]),
    PLATFORM_HEALTH_DETAIL: guard('/platform/health/:name', [PERMISSIONS.PLATFORM_HEALTH_VIEW]),
    PLATFORM_INFRASTRUCTURE: guard('/platform/infrastructure', [PERMISSIONS.PLATFORM_HEALTH_VIEW]),
    PLATFORM_METERING: guard('/platform/metering', [PERMISSIONS.PLATFORM_METERING_VIEW]),
    PLATFORM_METERING_USAGE_DETAIL: guard('/platform/metering/usage/:institutionId', [PERMISSIONS.PLATFORM_METERING_VIEW]),
    PLATFORM_BILLING_PLANS: guard('/platform/billing/plans', [PERMISSIONS.PLATFORM_BILLING_MANAGE]),
    PLATFORM_BILLING_INVOICES: guard('/platform/billing/invoices', [PERMISSIONS.PLATFORM_METERING_VIEW]),
    AI_REPORTS: guard('/ai/reports', [PERMISSIONS.REPORTS_VIEW]),
    AI_REPORTS_DETAIL: guard('/ai/reports/:id', [PERMISSIONS.REPORTS_VIEW]),
    AI_EXPLORER: guard('/ai/explorer', [PERMISSIONS.REPORTS_VIEW]),
    SEARCH: guard('/search', [PERMISSIONS.CUSTOMERS_VIEW]),
    ADMIN: open('/admin'),
    TRANSACTIONS: guard('/transactions', [PERMISSIONS.TRANSACTIONS_VIEW]),
    TRANSACTIONS_DETAIL: guard('/transactions/:id', [PERMISSIONS.TRANSACTIONS_VIEW]),
    ADMIN_INSURANCE_PRODUCTS: guard('/admin/insurance/products', [PERMISSIONS.INSURANCE_PRODUCTS_MANAGE]),
    ADMIN_INSURANCE_QUOTES: guard('/admin/insurance/quotes', [PERMISSIONS.INSURANCE_POLICIES_MANAGE]),
    ADMIN_INSURANCE_UNDERWRITING: guard('/admin/insurance/underwriting', [PERMISSIONS.INSURANCE_POLICIES_MANAGE]),
    ADMIN_INSURANCE_CLAIMS: guard('/admin/insurance/claims', [PERMISSIONS.INSURANCE_CLAIMS_MANAGE]),
    PLATFORM_MIGRATION: guard('/platform/migration', [PERMISSIONS.CONFIG_MANAGE]),
    SETTINGS: guard('/settings', [PERMISSIONS.SETTINGS_VIEW]),
    UNAUTHORIZED: open('/unauthorized'),
    CATCH_ALL: open('*'),
} as const satisfies Record<string, RouteDefinition>;

// ─── Navigation-only paths ───────────────────────────────────────────────────
// Linked from the sidebar and/or registered for search, but not declared as a
// `<Route>` in AppRoutes.
//
// The `/openbanking/*` entries are rendered by the nested `OpenBankingRoutes`
// router. `ROUTES.OPENBANKING_SECTION` is what actually enforces them, so they
// carry its permission rather than a per-link guess.

export const NAV_ONLY = {
    OPENBANKING_HOME: guard('/openbanking', [PERMISSIONS.ACCOUNTS_VIEW]),
    OPENBANKING_BRANDS: guard('/openbanking/brands', [PERMISSIONS.ACCOUNTS_VIEW]),
    OPENBANKING_TRANSACTIONS: guard('/openbanking/transactions', [PERMISSIONS.ACCOUNTS_VIEW]),
    OPENBANKING_WEBHOOKS: guard('/openbanking/webhooks', [PERMISSIONS.ACCOUNTS_VIEW]),
    OPENBANKING_ANALYTICS: guard('/openbanking/analytics', [PERMISSIONS.ACCOUNTS_VIEW]),
} as const satisfies Record<string, RouteDefinition>;

/** Everything the catalogue knows about, for lookup by path. */
export const ROUTE_CATALOGUE: readonly RouteDefinition[] = [
    ...Object.values(ROUTES),
    ...Object.values(NAV_ONLY),
];

// ─── Reading policy off an entry ─────────────────────────────────────────────

/**
 * Permissions for `<ProtectedRoute requiredPermissions>` and for sidebar
 * `requiredPermissions`. Unrestricted routes yield an empty list, which both
 * consumers already treat as "no permission gate".
 */
export function permissionsFor(route: RouteDefinition): PermissionName[] {
    return route.access.kind === 'permission' ? route.access.permissions : [];
}

/** Whether ANY (rather than ALL) of the permissions is sufficient. */
export function requiresAnyPermission(route: RouteDefinition): boolean {
    return route.access.kind === 'permission' && route.access.requireAny;
}

/**
 * The `{ path, requiredPermission }` pair the navigation registry needs.
 *
 * `NavItem.requiredPermission` holds at most one permission, so a route guarded
 * by several is registered without one. That field is display metadata only —
 * navigation authorization resolves a page's policy from its `path` through
 * this catalogue, so multi-permission routes are still fully gated.
 */
export function navTarget(route: RouteDefinition): {
    path: string;
    requiredPermission?: PermissionName;
} {
    const permissions = permissionsFor(route);
    return permissions.length === 1
        ? { path: route.path, requiredPermission: permissions[0] }
        : { path: route.path };
}

// ─── Path resolution ─────────────────────────────────────────────────────────

/** Strip query/hash and any trailing slash so paths compare consistently. */
function normalizePath(path: string): string {
    const withoutQuery = path.split(/[?#]/)[0];
    return withoutQuery.length > 1 && withoutQuery.endsWith('/')
        ? withoutQuery.slice(0, -1)
        : withoutQuery;
}

const segmentsOf = (path: string): string[] => path.split('/').filter(Boolean);

interface PatternMatch {
    /** Higher is more specific: static segments beat params, params beat `*`. */
    readonly score: number;
    /** Non-wildcard patterns always win over wildcard ones. */
    readonly exact: boolean;
}

/**
 * Match concrete URL segments against a route pattern.
 *
 * A pattern only matches when every segment lines up, so `/admin` never
 * absorbs `/admin/secret-console` — only an explicit `:param` or a trailing
 * `*` consumes extra segments.
 */
function matchPattern(pattern: string, target: string[]): PatternMatch | null {
    const patternSegments = segmentsOf(pattern);
    let score = 0;

    for (let i = 0; i < patternSegments.length; i++) {
        const segment = patternSegments[i];

        if (segment === '*') {
            // Trailing wildcard: consumes zero or more remaining segments.
            return { score: score + 1, exact: false };
        }
        if (i >= target.length) return null;
        if (segment.startsWith(':')) {
            score += 2;
            continue;
        }
        if (segment !== target[i]) return null;
        score += 3;
    }

    return patternSegments.length === target.length ? { score, exact: true } : null;
}

export type RouteResolution =
    | { known: false }
    | { known: true; route: RouteDefinition };

/**
 * Find the catalogue entry that governs a concrete URL.
 *
 * Picks the most specific match: a literal path beats a `:param` path, and both
 * beat a `/*` sub-router. Unknown paths resolve to `{ known: false }` so callers
 * can fail closed.
 */
export function resolveRouteForPath(path: string): RouteResolution {
    const target = normalizePath(path ?? '');
    if (!target.startsWith('/')) return { known: false };

    const segments = segmentsOf(target);
    let best: RouteDefinition | null = null;
    let bestMatch: PatternMatch | null = null;

    for (const route of ROUTE_CATALOGUE) {
        // The `*` catch-all is a redirect, not an authorization target.
        if (route.path === '*') continue;

        const match = matchPattern(route.path, segments);
        if (!match) continue;
        if (
            !bestMatch ||
            (match.exact && !bestMatch.exact) ||
            (match.exact === bestMatch.exact && match.score > bestMatch.score)
        ) {
            best = route;
            bestMatch = match;
        }
    }

    return best ? { known: true, route: best } : { known: false };
}
